import { useEffect, useState } from 'react';
import { useI18n } from '@/i18n';
import { dataService } from '@/services/dataService';
import { Creator, Partnership } from '@/types/entities';
import { CreatorFormDrawer, CreatorFormValues } from '@/modules/creators/CreatorFormDrawer';
import { PartnershipFormDrawer, PartnershipFormValues } from '@/modules/partnerships/PartnershipFormDrawer';
import { ContractFormDrawer, ContractFormValues } from '@/modules/contracts/ContractFormDrawer';
import { AgentFormDrawer, AgentFormValues } from '@/modules/agents/AgentFormDrawer';

type Kind = 'creator' | 'partnership' | 'contract' | 'followup' | 'agent' | 'content' | 'lead' | 'client' | 'project';
const ENABLED: Kind[] = ['creator', 'partnership', 'contract', 'followup', 'agent'];
const ALL: Kind[] = ['creator', 'partnership', 'contract', 'followup', 'agent', 'content', 'lead', 'client', 'project'];

/**
 * Every locally-supported entity actually creates a real (local) record.
 * Entities that genuinely aren't built yet (Content/Lead/Client/Project)
 * show "Coming Soon" and do nothing else — no silent failure, per the
 * brief's Quick Create requirement.
 */
export function QuickCreate({ onToast }: { onToast: (msg: string) => void }) {
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState<Kind | null>(null);
  const [followupCreatorId, setFollowupCreatorId] = useState('');
  const [followupTitle, setFollowupTitle] = useState('');
  const [creators, setCreators] = useState<Creator[]>([]);
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);

  useEffect(() => {
    if (active === 'followup' || active === 'contract') {
      dataService.listCreators().then(setCreators);
      dataService.listPartnerships().then(setPartnerships);
    }
  }, [active]);

  async function submitCreator(values: CreatorFormValues) {
    const created = await dataService.createCreator(values);
    onToast(`${t('toast.creatorAdded')} — ${created.id}`);
    setActive(null);
  }
  async function submitPartnership(values: PartnershipFormValues) {
    const created = await dataService.createPartnership(values);
    onToast(`${t('toast.partnershipCreated')} — ${created.id}`);
    setActive(null);
  }
  async function submitContract(values: ContractFormValues) {
    const created = await dataService.createContract(values);
    onToast(`${t('toast.contractCreated')} — ${created.id}`);
    setActive(null);
  }
  async function submitAgent(values: AgentFormValues) {
    const created = await dataService.createAgent(values);
    onToast(`${t('toast.agentAdded')} — ${created.name}`);
    setActive(null);
  }
  async function submitFollowup() {
    const creator = creators.find((c) => c.id === followupCreatorId);
    if (!creator || !followupTitle.trim()) return;
    const dueDate = new Date().toISOString().slice(0, 10);
    await dataService.createFollowup({ entityType: 'creator', entityId: creator.id, entityLabel: creator.fullName, title: followupTitle.trim(), dueDate, priority: 'medium', ownerId: creator.ownerId });
    onToast(t('toast.followupScheduled'));
    setFollowupTitle('');
    setFollowupCreatorId('');
    setActive(null);
  }

  return (
    <div className="popover-anchor">
      <button className="btn-primary" onClick={() => setMenuOpen((o) => !o)}>+ {t('topbar.quickCreate')}</button>
      {menuOpen && (
        <>
          <div className="popover-backdrop" onClick={() => setMenuOpen(false)} />
          <div className="popover-panel">
            <div className="popover-title">{t('quickCreate.title')}</div>
            {ALL.map((kind) => (
              <div
                key={kind}
                className={`popover-item ${ENABLED.includes(kind) ? '' : 'disabled'}`}
                onClick={() => {
                  if (!ENABLED.includes(kind)) return;
                  setMenuOpen(false);
                  setActive(kind);
                }}
              >
                <span>{t(`quickCreate.${kind}`)}</span>
                {!ENABLED.includes(kind) && <span className="soon-tag-inline">{t('common.comingSoon')}</span>}
              </div>
            ))}
          </div>
        </>
      )}

      {active === 'creator' && <CreatorFormDrawer onClose={() => setActive(null)} onSubmit={submitCreator} />}
      {active === 'partnership' && <PartnershipFormDrawer onClose={() => setActive(null)} onSubmit={submitPartnership} />}
      {active === 'contract' && <ContractFormDrawer partnerships={partnerships} onClose={() => setActive(null)} onSubmit={submitContract} />}
      {active === 'agent' && <AgentFormDrawer onClose={() => setActive(null)} onSubmit={submitAgent} />}
      {active === 'followup' && (
        <>
          <div className="drawer-backdrop" onClick={() => setActive(null)} />
          <div className="drawer-panel">
            <div style={{ fontWeight: 700, marginBottom: 12 }}>{t('quickCreate.followup')}</div>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>{t('followupForm.creator')}</label>
            <select className="text-input" value={followupCreatorId} onChange={(e) => setFollowupCreatorId(e.target.value)} style={{ marginBottom: 10 }}>
              <option value="">—</option>
              {creators.map((c) => <option key={c.id} value={c.id}>{c.id} — {c.fullName}</option>)}
            </select>
            <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>{t('followupForm.title')}</label>
            <input className="text-input" value={followupTitle} onChange={(e) => setFollowupTitle(e.target.value)} style={{ marginBottom: 14 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={submitFollowup}>{t('common.save')}</button>
              <button onClick={() => setActive(null)}>{t('common.cancel')}</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
